<script setup lang="ts">
  import { toRef } from 'vue';
  import { FieldSearchBar, NoDocuments, SearchToolbar, SelectInput } from '@/shared/ui';
  import { useTableFilterModal } from '@/shared/lib/use';
  import type {
    TrainingCollaboratorFilters,
    TrainingCollaboratorListItem,
  } from '@/entities/Training';
  import { useTrainingCollaborators } from '../../model/useTrainingCollaborators';
  import {
    COLLABORATOR_QUANTITY_OPTIONS,
    COLLABORATOR_SEARCH_FIELDS,
  } from '../../model/useTrainingCollaboratorsTable';
  import { useCertificateUpload } from '../../model/useCertificateUpload';
  import { useCollaboratorCertificateViewer } from '../../model/useCollaboratorCertificateViewer';
  import { useCertificateGeneration } from '../../model/useCertificateGeneration';
  import TrainingCollaboratorsTable from './TrainingCollaboratorsTable.vue';
  import TrainingCollaboratorsFilterModal from './TrainingCollaboratorsFilterModal.vue';

  const props = withDefaults(
    defineProps<{
      trainingId: number | null;
      /**
       * Bumped by the page whenever the training is completed. The list is paginated,
       * sorted and searched on the server, so it is refetched instead of patched from
       * the completion response, which carries only the unpaginated trainees.
       */
      reloadToken?: number;
    }>(),
    { reloadToken: 0 }
  );

  const {
    items,
    allItems,
    metadata,
    error,
    isInitialLoading,
    loadingMore,
    hasMoreItems,
    itemsPerPage,
    term,
    searchField,
    sort,
    order,
    filters,
    showControls,
    changePage,
    changeItemsPerPage,
    handleSearch,
    handleSearchFieldChange,
    handleSortUpdate,
    applyFilters,
    changeStatus,
    isChangingStatus,
    fetch,
  } = useTrainingCollaborators(toRef(props, 'trainingId'), toRef(props, 'reloadToken'));

  const { isFilterActive, openFilterModal } = useTableFilterModal<TrainingCollaboratorFilters>(
    TrainingCollaboratorsFilterModal,
    { clearFilters: () => {}, filters: {} },
    applyFilters,
    () => filters.value
  );

  const { openCertificateUpload } = useCertificateUpload(() => {
    fetch();
  });

  const { openCertificate } = useCollaboratorCertificateViewer();

  const { generatingIds, openCertificateGeneration } = useCertificateGeneration(() => {
    fetch();
  });

  function handleGenerateCertificate(collaborator: TrainingCollaboratorListItem): void {
    if (props.trainingId === null) return;

    void openCertificateGeneration(props.trainingId, {
      memberTrainingId: collaborator.id,
      hasCertificate: collaborator.hasCertificate,
    });
  }

  function handleUploadCertificate(collaborator: TrainingCollaboratorListItem): void {
    if (props.trainingId === null) return;

    openCertificateUpload(props.trainingId, {
      memberTrainingId: collaborator.id,
      hasCertificate: collaborator.hasCertificate,
    });
  }

  function handleViewCertificate(collaborator: TrainingCollaboratorListItem): void {
    if (props.trainingId === null) return;

    void openCertificate(props.trainingId, {
      memberTrainingId: collaborator.id,
      certificateMimeType: collaborator.certificateMimeType,
      certificateOriginalFileName: collaborator.certificateOriginalFileName,
    });
  }
</script>

<template>
  <div data-testid="training-collaborators-tab" class="flex flex-col">
    <div
      v-if="error"
      data-testid="training-collaborators-error-state"
      class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
    >
      {{ error }}
    </div>

    <div
      v-else-if="isInitialLoading"
      data-testid="training-collaborators-loading-state"
      class="flex items-center justify-center py-12"
    >
      <span class="text-sm text-gray-05">Carregando colaboradores...</span>
    </div>

    <template v-else-if="showControls">
      <SearchToolbar class="mb-6">
        <template #search>
          <FieldSearchBar
            data-testid="training-collaborators-search-bar"
            :search-fields="COLLABORATOR_SEARCH_FIELDS"
            independent-search-field
            :clear-search-on-field-change="false"
            :initial-search-term="term"
            :search-field-id="searchField"
            class="w-full"
            @update:search-field-id="handleSearchFieldChange"
            @search="handleSearch"
          />
        </template>
        <template #quantity>
          <SelectInput
            data-testid="training-collaborators-items-per-page"
            :model-value="itemsPerPage"
            :options="COLLABORATOR_QUANTITY_OPTIONS"
            arrow-class="w-5 h-5"
            shadow-class="shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]"
            @update:model-value="changeItemsPerPage"
          />
        </template>
      </SearchToolbar>

      <TrainingCollaboratorsTable
        :collaborators="items"
        :all-collaborators="allItems"
        :metadata="metadata"
        :items-per-page="itemsPerPage"
        :current-sort="sort"
        :current-order="order"
        :loading-more="loadingMore"
        :has-more-items="hasMoreItems"
        :is-filter-active="isFilterActive"
        :is-changing-status="isChangingStatus"
        :generating-ids="[...generatingIds]"
        @change-page="changePage"
        @update:sort="handleSortUpdate"
        @filter-clicked="openFilterModal"
        @change-status="changeStatus"
        @upload-certificate="handleUploadCertificate"
        @view-certificate="handleViewCertificate"
        @generate-certificate="handleGenerateCertificate"
      />
    </template>

    <NoDocuments
      v-else
      data-testid="training-collaborators-empty-state"
      title="Nenhum colaborador vinculado"
    />
  </div>
</template>
